import { useDispatch, useSelector } from "react-redux";
import { setSnackbar } from "reduxStore/state";
import {
  createPrincipleQuestion,
  fetchPrincipleQuestions,
  updatePrincipleQuestion,
} from "reduxStore/actions/principleQuestionsActions";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { searchPrinciples } from "reduxStore/actions/principleActions";
import { debounce } from "lodash";
import { fetchAllLocations } from "reduxStore/actions/locationActions";
import { getPermissionFlags, getUserScope } from "utils/permissions";

export const useQuestionsForm = ({
  editMode,
  setOpen,
  currentPrincipleQuestion,

  page,
  limit,
  search,
  sort,
  searchInput,
  location,
}) => {
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);
  const { organisationId, locationIds } = getUserScope(user);
  const locationId = locationIds[0] || "";

  const principleSearchResults = useSelector(
    (state) => state.principleSearchResults
  );

  const availableLocations = useSelector((state) => state.locationsAll);

  const { showOrg } = getPermissionFlags(user);

  const validationSchema = Yup.object().shape({
    question: Yup.string().required("Question is required"),
    principle: Yup.object().nullable().required("Principle is required"),
    url: Yup.string().url("Must be a valid URL"),
  });

  const initialValues = {
    question: currentPrincipleQuestion ? currentPrincipleQuestion.question : "",
    principle: currentPrincipleQuestion
      ? currentPrincipleQuestion.principle
      : {},
    location: currentPrincipleQuestion ? currentPrincipleQuestion.location : [], // Ensure locations are an array
    url: currentPrincipleQuestion?.url || "", // Ensure url is always a string
    priority:
      currentPrincipleQuestion?.priority === true ||
      currentPrincipleQuestion?.priority === false
        ? currentPrincipleQuestion.priority
        : false,
  };

  const [searchPrincipleTerm, setSearchPrincipleTerm] = useState("");

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      values.organisation = organisationId;

      if (!editMode) {
        values.location = showOrg
          ? values.location.map((loc) => loc._id)
          : locationId
            ? [locationId]
            : [];
      } else {
        delete values.location;
      }

      let response;
      if (editMode) {
        response = await dispatch(
          updatePrincipleQuestion(currentPrincipleQuestion._id, values)
        );
      } else {
        response = await dispatch(createPrincipleQuestion(values));
      }

      if (response?.error) {
        throw new Error(response.message || "An unknown error occurred");
      }

      dispatch(
        setSnackbar({
          open: true,
          message: editMode
            ? "Principle Question updated successfully"
            : "Principle Question created successfully",
          severity: "success",
        })
      );

      setOpen(false);

      dispatch(
        fetchPrincipleQuestions({
          location,
          search,
          sort,
          page: page || 1,
          limit: limit || 10,
        })
      );
    } catch (error) {
      console.error("Error in handleSubmit:", error);

      dispatch(
        setSnackbar({
          open: true,
          message:
            error.message ||
            "An error occurred while saving the principle question",
          severity: "error",
        })
      );
    } finally {
      setOpen(false);
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const debounceFetch = debounce(() => {
      if (searchPrincipleTerm) {
        dispatch(searchPrinciples(searchPrincipleTerm));
      }
    }, 300);
    debounceFetch();

    return () => {
      debounceFetch.cancel();
    };
  }, [searchPrincipleTerm, dispatch]);

  useEffect(() => {
    dispatch(fetchAllLocations());
  }, [dispatch]);

  return {
    validationSchema,
    initialValues,
    handleSubmit,
    principleSearchResults,
    setSearchPrincipleTerm,
    availableLocations,
    showOrg,
  };
};
